import { delay, EventListener, isArrayOfType, ManagedPromise, objectEntries } from '../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../domain'

import type { BackendEventStreamResponseContent, BackendPath, BackendQueryParameters } from '../Connectware'

export type RSTWebSocketWrapperOptions<Path extends BackendPath> = Readonly<{
    /**
     * Socket url origin
     */
    origin: URL['origin']
    /**
     * Socket url path
     */
    path: Path
    /**
     * Socket url params without the secret token
     */
    params: BackendQueryParameters<Path>
    /**
     * Reconnection cool down period in milliseconds
     */
    reconnectTimeout: number
}>

/**
 * The minimal naked websocket internally used and wrapped for usage elsewhere in the application
 *
 * This type helps out with tests, so it is known exactly is wanted and used
 */
export type InternalWebSocket = Readonly<{
    readonly url: string

    /**
     * @see {WebSocket.addEventListener}
     */
    addEventListener(type: 'open', handler: VoidFunction, options: { once: true }): void

    /**
     * @see {WebSocket.addEventListener}
     */
    addEventListener<P extends BackendPath>(type: 'message', handler: (e: Pick<MessageEvent<BackendEventStreamResponseContent<P>>, 'data'>) => void): void

    /**
     * @see {WebSocket.addEventListener}
     */
    addEventListener(type: 'close', handler: (event: Readonly<{ reason: string }>) => void, options: { once: true }): void

    /**
     * @see {WebSocket.addEventListener}
     */
    addEventListener(type: 'error', handler: VoidFunction, options: { once: true }): void

    /**
     * @see {WebSocket.close}
     */
    close(code: 1000, reason: string): void
}>

/**
 * Wrapped websocket to be used from the rest of the application
 */
export type RSTWrappedWebSocket<Path extends BackendPath> = Readonly<{
    on(listener: (event: BackendEventStreamResponseContent<Path> | ConnectwareError) => void): Promise<VoidFunction>
}>

export class RSTWebSocketWrapper<P extends BackendPath> implements RSTWrappedWebSocket<P> {
    protected static readonly INTERNAL_CLOSURE_REASON = `${this.name}_INTERNAL_CLOSURE_REASON`

    private readonly listener = new EventListener<BackendEventStreamResponseContent<P> | ConnectwareError>()

    private socket: InternalWebSocket | null = null

    protected readonly InternalWebSocketConstructor: new (url: URL) => InternalWebSocket = WebSocket

    constructor (private readonly options: RSTWebSocketWrapperOptions<P>) {}

    private hasSocket (): boolean {
        return this.socket !== null
    }

    private needsSocket (): boolean {
        return this.listener.size > 0
    }

    private createURL (): URL {
        const { origin, path, params } = this.options

        const url = new URL(origin)

        url.pathname = path

        for (const [key, valueOrArray] of objectEntries(params)) {
            for (const value of isArrayOfType(valueOrArray, (v): v is string => typeof v === 'string') ? valueOrArray : [valueOrArray]) {
                url.searchParams.append(String(key), String(value))
            }
        }

        return url
    }

    /**
     * Discards current socket without care for what it was supposed to be doing
     */
    private discardSocket (): void {
        /**
         * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close
         * @see https://developer.mozilla.org/pt-BR/docs/Web/API/CloseEvent
         */
        this.socket?.close(1_000, RSTWebSocketWrapper.INTERNAL_CLOSURE_REASON)
        this.socket = null
    }

    /**
     * Replaces current socket without care for what it was supposed to be doing
     */
    private async replaceSocket (): Promise<void> {
        this.discardSocket()

        /**
         * So a cool down period before trying again
         */
        await delay(this.options.reconnectTimeout)

        if (this.hasSocket() || !this.needsSocket()) {
            /**
             * There is already a socket there, or there is no need for one
             */
            return
        }

        return this.initializeSocket()
    }

    /**
     * Creates a socket if there was none there
     * And setups listeners to recreate another socket
     */
    private initializeSocket (): Promise<void> {
        if (this.hasSocket()) {
            /**
             * Socket is already there, so don't bother setting everything up again
             */
            return Promise.resolve()
        }

        const promise = new ManagedPromise<void, ConnectwareError>()
        const socket = new this.InternalWebSocketConstructor(this.createURL())
        this.socket = socket

        socket.addEventListener(
            'open',
            () => {
                socket.addEventListener(
                    'close',
                    (e) => {
                        if (e.reason !== RSTWebSocketWrapper.INTERNAL_CLOSURE_REASON) {
                            /** Close is not internal, so create another socket to reconnect */
                            void this.replaceSocket()
                        }
                    },
                    { once: true }
                )

                /**
                 * Deals with the messages that come back from RST
                 */
                socket.addEventListener<P>('message', (e) => {
                    let data: BackendEventStreamResponseContent<P> | ConnectwareError

                    try {
                        data = JSON.parse(String(e.data))
                    } catch (err: unknown) {
                        data = new ConnectwareError(ConnectwareErrorType.MAPPING_ERROR, 'Given data is not parseable', {
                            url: socket.url,
                            data: e.data,
                            message: (err as ConnectwareError).message,
                        })
                    }

                    this.listener.trigger(data)
                })

                promise.resolve()
            },
            { once: true }
        )

        /**
         * There was a error with the connection, so retry again
         */
        socket.addEventListener('error', () => promise.resolve(this.replaceSocket()), { once: true })

        return promise.promise
    }

    private disuseSocket (): void {
        if (this.needsSocket()) {
            /** Socket in use, keep it around */
            return
        }

        this.discardSocket()
    }

    on (listener: (e: BackendEventStreamResponseContent<P> | ConnectwareError) => void): Promise<VoidFunction> {
        const off = this.listener.on(listener)

        return this.initializeSocket().then(() => {
            return () => {
                off()

                this.disuseSocket()
            }
        })
    }
}
